import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Breadcrumbs from '../../components/Breadcrumbs';

const PageContainer = styled.div`
  padding-top: 80px;
`;

const Hero = styled.section`
  position: relative;
  padding: 8rem 2rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;
  overflow: hidden;
  text-align: center;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(99, 102, 241, 0.15) 0%, transparent 70%),
      radial-gradient(circle at 80% 70%, rgba(244, 63, 94, 0.15) 0%, transparent 70%);
    opacity: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Badge = styled(motion.span)`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: rgba(99, 102, 241, 0.1);
  color: #818cf8;
  border-radius: 30px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(99, 102, 241, 0.2);
  backdrop-filter: blur(10px);
`;

const Title = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 800;
  background: linear-gradient(135deg, #818cf8 0%, #e879f9 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #94a3b8;
  margin-bottom: 3rem;
  line-height: 1.7;
  max-width: 600px;
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(135deg, #818cf8 0%, #e879f9 100%);
  color: white;
  padding: 1.2rem 2.5rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(99, 102, 241, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(99, 102, 241, 0.4);
  }
`;

const IntegrationGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
`;

const IntegrationIcon = styled(motion.div)`
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin: 0 auto;
  border: 1px solid rgba(99, 102, 241, 0.2);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(99, 102, 241, 0.4);
  }
`;

const FeaturesSection = styled.section`
  padding: 8rem 2rem;
  background: #f8fafc;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(99, 102, 241, 0.05) 0%, transparent 70%),
      radial-gradient(circle at 80% 70%, rgba(244, 63, 94, 0.05) 0%, transparent 70%);
  }
`;

const FeaturesGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem;
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled(motion.div)`
  background: white;
  padding: 2.5rem;
  border-radius: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(99, 102, 241, 0.08);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.05);
    border-color: rgba(99, 102, 241, 0.2);
  }
`;

const FeatureIconWrapper = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
`;

const FeatureIcon = styled.div`
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(244, 63, 94, 0.1) 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  position: relative;
  z-index: 1;

  &:before {
    content: '';
    position: absolute;
    inset: -2px;
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(244, 63, 94, 0.2));
    border-radius: inherit;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  ${FeatureCard}:hover & {
    &:before {
      opacity: 1;
    }
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
  position: relative;
`;

const FeatureDescription = styled.p`
  color: #64748b;
  line-height: 1.7;
  font-size: 1.1rem;
`;

const WorkflowSection = styled.section`
  padding: 6rem 2rem;
  background: #f8fafc;
`;

const WorkflowContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const WorkflowStep = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 2rem;
  margin-bottom: 4rem;
  position: relative;

  &:not(:last-child):before {
    content: '';
    position: absolute;
    top: 80px;
    left: 24px;
    bottom: -60px;
    width: 2px;
    background: linear-gradient(to bottom, #6366f1, #f43f5e);
  }
`;

const StepNumber = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #6366f1 0%, #f43f5e 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.25rem;
  flex-shrink: 0;
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const StepDescription = styled.p`
  color: #64748b;
  line-height: 1.7;
  font-size: 1.1rem;
`;

const CTASection = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(99, 102, 241, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(244, 63, 94, 0.1) 0%, transparent 50%);
  }
`;

const CTAContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #6366f1 0%, #f43f5e 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const CTAText = styled.p`
  font-size: 1.25rem;
  color: #94a3b8;
  margin-bottom: 2rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 5rem;
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 1rem;
  
  span {
    background: linear-gradient(135deg, #818cf8 0%, #e879f9 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  color: #64748b;
  line-height: 1.7;
  max-width: 600px;
  margin: 0 auto;
`;

const SectionCTAButton = styled(motion.button)`
  background: linear-gradient(135deg, #818cf8 0%, #e879f9 100%);
  color: white;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  margin-top: 3rem;
  box-shadow: 0 4px 6px rgba(99, 102, 241, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(99, 102, 241, 0.4);
  }
`;

const SocialProofSection = styled.section`
  padding: 8rem 2rem;
  background: white;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(99, 102, 241, 0.03) 0%, transparent 70%),
      radial-gradient(circle at 80% 70%, rgba(244, 63, 94, 0.03) 0%, transparent 70%);
  }
`;

const TestimonialsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto 4rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled(motion.div)`
  background: white;
  padding: 2.5rem;
  border-radius: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(99, 102, 241, 0.08);
  position: relative;

  &:hover {
    border-color: rgba(99, 102, 241, 0.2);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  }
`;

const TestimonialContent = styled.blockquote`
  font-size: 1.125rem;
  color: #1e293b;
  line-height: 1.7;
  margin-bottom: 2rem;
  font-style: italic;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AuthorAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #818cf8 0%, #e879f9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1.125rem;
`;

const AuthorInfo = styled.div`
  flex: 1;
`;

const AuthorName = styled.div`
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
`;

const AuthorRole = styled.div`
  color: #64748b;
  font-size: 0.875rem;
`;

const CompanyLogos = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4rem;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    gap: 2rem;
  }
`;

const CompanyLogo = styled(motion.div)`
  color: #64748b;
  font-weight: 700;
  font-size: 1.25rem;
  opacity: 0.7;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1;
  }
`;

// Add new styled components for metrics
const MetricsSection = styled.section`
  padding: 4rem 2rem;
  background: white;
  position: relative;
  z-index: 1;
`;

const MetricsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const MetricCard = styled(motion.div)`
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(99, 102, 241, 0.1);
`;

const MetricValue = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #818cf8 0%, #e879f9 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.5rem;
`;

const MetricLabel = styled.div`
  color: #64748b;
  font-size: 1rem;
  font-weight: 500;
`;

function IntegrationTesting() {
  return (
    <PageContainer>
      <Breadcrumbs items={[
        { label: 'Services', path: '/services' },
        { label: 'Integration Testing', path: '/services/integration' }
      ]} />
      <Hero>
        <HeroContent>
          <Badge
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            Enterprise Integration Testing
          </Badge>
          <Title
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Power Your Business with Seamless Integrations
          </Title>
          <Subtitle>
            Ensure flawless connectivity between all your systems and third-party applications.
            Prevent integration issues before they impact your revenue.
          </Subtitle>
          <CTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Get Started Free
          </CTAButton>
        </HeroContent>
      </Hero>

      <MetricsSection>
        <MetricsGrid>
          {[
            { value: '99.9%', label: 'Integration Success Rate' },
            { value: '85%', label: 'Faster Issue Resolution' },
            { value: '24/7', label: 'Continuous Monitoring' },
            { value: '100+', label: 'Integration Partners' }
          ].map((metric, index) => (
            <MetricCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <MetricValue>{metric.value}</MetricValue>
              <MetricLabel>{metric.label}</MetricLabel>
            </MetricCard>
          ))}
        </MetricsGrid>
      </MetricsSection>

      <FeaturesSection>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Advanced <span>Integration Features</span>
          </SectionTitle>
          <SectionSubtitle>
            Enterprise-grade testing solutions to ensure your integrations work flawlessly at scale
          </SectionSubtitle>
        </SectionHeader>
        <FeaturesGrid>
          {[
            {
              icon: '⚡',
              title: 'API Testing',
              description: 'Comprehensive testing of all API endpoints and integrations with detailed reporting and monitoring.'
            },
            {
              icon: '🔌',
              title: 'Third-party Apps',
              description: 'Verify compatibility and performance with all your essential third-party applications and services.'
            },
            {
              icon: '📊',
              title: 'Data Sync',
              description: 'Ensure seamless data synchronization across all integrated systems with real-time validation.'
            },
            {
              icon: '🛡️',
              title: 'Error Handling',
              description: 'Robust error handling and recovery testing for all integrations with automated recovery procedures.'
            },
            {
              icon: '🔄',
              title: 'Continuous Testing',
              description: 'Automated testing pipelines that continuously validate your integration points and workflows.'
            },
            {
              icon: '📱',
              title: 'Cross-Platform',
              description: 'Test integrations across all platforms and devices to ensure consistent functionality.'
            }
          ].map((feature, index) => (
            <FeatureCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <FeatureIconWrapper>
                <FeatureIcon>{feature.icon}</FeatureIcon>
              </FeatureIconWrapper>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesGrid>
        <div style={{ textAlign: 'center', marginTop: '4rem' }}>
          <SectionCTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            View Full Feature List
          </SectionCTAButton>
        </div>
      </FeaturesSection>

      <WorkflowSection>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Streamlined <span>Testing Process</span>
          </SectionTitle>
          <SectionSubtitle>
            Our battle-tested workflow ensures comprehensive testing and validation of all integration points
          </SectionSubtitle>
        </SectionHeader>
        <WorkflowContainer>
          {[
            {
              number: '01',
              title: 'Integration Mapping',
              description: 'We create a comprehensive map of all your integrations and their dependencies.'
            },
            {
              number: '02',
              title: 'Test Case Development',
              description: 'Custom test cases are developed for each integration point and scenario.'
            },
            {
              number: '03',
              title: 'Automated Testing',
              description: 'Continuous automated testing ensures integrations stay working perfectly.'
            },
            {
              number: '04',
              title: 'Monitoring & Alerts',
              description: 'Real-time monitoring and instant alerts for any integration issues.'
            }
          ].map((step, index) => (
            <WorkflowStep
              key={index}
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <StepNumber>{step.number}</StepNumber>
              <StepContent>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepContent>
            </WorkflowStep>
          ))}
        </WorkflowContainer>
        <div style={{ textAlign: 'center', marginTop: '4rem' }}>
          <SectionCTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Schedule a Demo
          </SectionCTAButton>
        </div>
      </WorkflowSection>

      <SocialProofSection>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Trusted by <span>Global Leaders</span>
          </SectionTitle>
          <SectionSubtitle>
            Join thousands of businesses that rely on our integration testing platform
          </SectionSubtitle>
        </SectionHeader>
        <TestimonialsGrid>
          {[
            {
              content: "The automated integration testing has saved us countless hours and helped us maintain 99.9% uptime during peak sales events. Highly recommended!",
              author: "Sarah Chen",
              role: "CTO at TechCommerce",
              initials: "SC"
            },
            {
              content: "Since implementing their integration testing solution, we've reduced integration-related issues by 85% and significantly improved our deployment confidence.",
              author: "Michael Rodriguez",
              role: "Head of Engineering at ShopifyPlus",
              initials: "MR"
            }
          ].map((testimonial, index) => (
            <TestimonialCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <TestimonialContent>"{testimonial.content}"</TestimonialContent>
              <TestimonialAuthor>
                <AuthorAvatar>{testimonial.initials}</AuthorAvatar>
                <AuthorInfo>
                  <AuthorName>{testimonial.author}</AuthorName>
                  <AuthorRole>{testimonial.role}</AuthorRole>
                </AuthorInfo>
              </TestimonialAuthor>
            </TestimonialCard>
          ))}
        </TestimonialsGrid>
        <CompanyLogos>
          {[
            "SHOPIFY+",
            "AMAZON",
            "WALMART",
            "ETSY",
            "EBAY",
            "BIGCOMMERCE"
          ].map((company, index) => (
            <CompanyLogo
              key={index}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 0.7 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              {company}
            </CompanyLogo>
          ))}
        </CompanyLogos>
        <div style={{ textAlign: 'center', marginTop: '4rem' }}>
          <SectionCTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Read Customer Stories
          </SectionCTAButton>
        </div>
      </SocialProofSection>

      <CTASection>
        <CTAContent>
          <CTATitle>Ready to Transform Your Integration Testing?</CTATitle>
          <CTAText>
            Get a personalized demo and see how our platform can streamline your integration testing process.
          </CTAText>
          <CTAButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Book Your Demo Today
          </CTAButton>
        </CTAContent>
      </CTASection>
    </PageContainer>
  );
}

export default IntegrationTesting;